// Light/dark theme toggle. Persists to localStorage; sets `data-theme` on
// <html> so CSS can react via `[data-theme="light"]` / `[data-theme="dark"]`.
//
// Default = dark, matching the source-of-truth portal.

import { renderIcon } from "@tkos/shared-ui";

const STORAGE_KEY = "tkos.theme";

export type Theme = "light" | "dark";

function readStored(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" ? v : null;
  } catch {
    return null;
  }
}

function writeStored(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // ignore — Safari private mode etc.
  }
}

/** Apply a theme by setting `data-theme` on <html>. */
function apply(theme: Theme): void {
  document.documentElement.setAttribute("data-theme", theme);
}

/**
 * Get the active theme. Reads from <html data-theme> if set, else
 * localStorage, else defaults to "dark".
 */
export function getTheme(): Theme {
  const attr = document.documentElement.getAttribute("data-theme");
  if (attr === "light" || attr === "dark") return attr;
  return readStored() ?? "dark";
}

/** Set + persist + apply the theme. */
export function setTheme(theme: Theme): void {
  apply(theme);
  writeStored(theme);
}

/** Toggle between light and dark. Returns the new theme. */
export function toggleTheme(): Theme {
  const next: Theme = getTheme() === "dark" ? "light" : "dark";
  setTheme(next);
  return next;
}

/**
 * Initialise the theme from storage (or the default) before the chrome
 * renders. Safe to call multiple times.
 */
export function initTheme(): void {
  apply(readStored() ?? "dark");
}

/**
 * Build a small button that toggles theme. Returns the element so callers
 * can append it wherever (typically the top bar).
 */
export function createThemeToggle(): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "tkos-theme-toggle";
  btn.setAttribute("aria-label", "Toggle light / dark theme");
  btn.title = "Toggle theme";

  const refresh = (): void => {
    const t = getTheme();
    // Show the icon for the OPPOSITE theme (i.e. "click here to switch to X").
    btn.replaceChildren(renderIcon(t === "dark" ? "sun" : "moon", { size: "sm" }));
    btn.setAttribute("data-theme-state", t);
  };
  refresh();

  btn.addEventListener("click", () => {
    toggleTheme();
    refresh();
  });

  return btn;
}
